import { combineAccessInputs, mapAccessInput, type AccessInput } from '@/abilities/accessInput';
import { createCaseAbility } from '@/abilities/createCaseAbility';
import { treeDestinationAccess, type DestinationAccess } from '@/abilities/destination';
import {
  companyDashboardAccessContext,
  type CompanyDashboardAccessContext,
  type CompanyDashboardAccessValues,
} from '@/abilities/companyDashboard/context';
import { companyDashboardRules } from '@/abilities/companyDashboard/rules';
import {
  companyDashboardDestinationTree,
  toCompanyDashboardDestination,
  type CompanyDashboardAction,
  type CompanyDashboardDestination,
  type CompanyDashboardDestinationKey,
} from '@/abilities/companyDashboard/vocabulary';

/**
 * How the user may reach each destination of the company dashboard. The only answer the case gives: a CASL check on the
 * page itself would find no rule and always say no.
 */
export type CompanyDashboardAccess = {
  readonly accessOf: (destination: CompanyDashboardDestinationKey) => DestinationAccess;
};

export type CompanyDashboardAccessInputs = {
  readonly [Key in keyof CompanyDashboardAccessValues]: AccessInput<CompanyDashboardAccessValues[Key]>;
};

export const companyDashboardAccessOf = (ctx: CompanyDashboardAccessContext): CompanyDashboardAccess => ({
  accessOf: treeDestinationAccess(
    createCaseAbility<CompanyDashboardAction, CompanyDashboardDestination>(companyDashboardRules(ctx)),
    toCompanyDashboardDestination,
    companyDashboardDestinationTree,
  ),
});

export const companyDashboardAccess = (inputs: CompanyDashboardAccessInputs): AccessInput<CompanyDashboardAccess> =>
  mapAccessInput(combineAccessInputs(inputs), (values) =>
    companyDashboardAccessOf(companyDashboardAccessContext(values)),
  );
